import React, { useMemo } from "react";
import RestockSidebar from "./RestockSidebar";
import RestockActions from "./RestockSidebar/RestockActions";
import { RestockApiResult, useRestockWorkflow } from "../../../hooks/useRestockWorkflow";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../lib/store/storeConfig";
import { addMessage } from "../../../lib/store/reducers/responseMessagesReducer";
import { BOX_ITEM_VERIFICATION_ERRORS, SHELF_SCAN_ERRORS } from "../../../utils/Constants";
import { useSelector } from "react-redux";
import {
  resetRestockState,
  restockStateSelector,
  setActiveRestockItemId,
  setRestockContextId,
  upsertRestockItem,
} from "../../../lib/store/reducers/restockReducer";
import { detectBarcodeType, isSatisfied } from "../../../utils/helpers/validation";
import { assignPrefix } from "../../../utils/helpers/barcode";
import { currentBoxSelector } from "../../../lib/store/reducers/conveyorBoxReviewReducer";
import {
  IItemRestockRequest,
  IItemRestockResponse,
} from "../../../lib/conveyorbox/ConveyorBoxClient";
import { getItemByIdentifier } from "../../../utils/helpers/conveyor-box";
import { conveyorBoxEndpoints } from "../../../lib/conveyorbox/endpoints";
import RestockItem from "../BoxItem/RestockItem";
import { IRestockItemUI } from "../../../lib/interfaces/ConveyorBox/IConveyorBox";
import {
  triggerOrderOverviewStage,
  triggerResetStage,
} from "../../../lib/store/reducers/globalStateReducer";

type RestockMeta = { deliveryId: number };
const RestockOverview = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { items, activeContextId, activeItemId } = useSelector(restockStateSelector);

  const { boxBarcode } = useSelector(currentBoxSelector);
  const { scanItem, scanLocationAndRestock } = useRestockWorkflow<
    IRestockItemUI,
    unknown,
    IItemRestockRequest,
    RestockMeta
  >({
    notify: (msg) => {
      dispatch(
        addMessage({
          messageType: msg.type,
          title: msg.title,
          content: msg.content,
        })
      );
    },
    items,
    selectedItemId: activeItemId,
    setSelectedItemId: (id) => dispatch(setActiveRestockItemId(id as any)),
    activeDeliveryId: activeContextId,
    setActiveDeliveryId: (id) => dispatch(setRestockContextId(id as any)),

    findItemByScan: (items, scannedBarcode) => getItemByIdentifier(items, scannedBarcode),
    getItemId: (item) => item.id,

    isVerificationSatisfied: (item) => isSatisfied(item.quantity, item.quantityVerified),

    incrementVerified: (item) => ({
      ...item,
      quantityVerified: item.quantityVerified + 1,
    }),

    upsertItem: (item) => dispatch(upsertRestockItem(item)),

    formatLocationBarcode: (raw) => assignPrefix(raw, "H").toUpperCase(),
    isValidLocationBarcode: (formatted) => detectBarcodeType(formatted) === "SHELF_NUMBER",
    invalidLocationMessage: SHELF_SCAN_ERRORS.INCORRECT_TYPE,

    buildPayload: ({ item, formattedLocation, activeDeliveryId }) => {
      const payload: IItemRestockRequest = {
        deliveryId: activeDeliveryId,
        itemId: item.id,
        scannedQuantity: item.quantityVerified,
        missingQuantity: 0,
        operatorAssignedShelf: formattedLocation,
      };
      return payload;
    },

    restockApiCall: async (payload): Promise<RestockApiResult<{ deliveryId: number }>> => {
      const res: IItemRestockResponse = await conveyorBoxEndpoints.restock(boxBarcode, payload);
      const { isValid, requestedShelf, suggestedShelf, deliveryId } = res;

      if (!isValid) {
        return {
          ok: false,
          reason: "INVALID_LOCATION",
          requested: requestedShelf,
          suggested: suggestedShelf,
        };
      }

      return { ok: true, meta: { deliveryId } };
    },

    onRestockSuccess: ({ item, formattedLocation, apiMeta }) => {
      const updated = { ...item };
      updated.quantity -= updated.quantityVerified;

      if (updated.quantity <= 0) {
        updated.isRestocked = true;
        updated.operatorAssignedShelf = formattedLocation;
        return {
          updatedItem: updated,
          clearSelection: true,
          nextDeliveryId: apiMeta?.deliveryId,
        };
      }

      return {
        updatedItem: updated,
        nextDeliveryId: apiMeta?.deliveryId,
      };
    },
  });

  const onScanItemHandler = (barcode: string) =>
    scanItem(barcode, {
      alreadySelected: BOX_ITEM_VERIFICATION_ERRORS.FORCE_CONTINUE_WITH_SCANNED_ITEM,
      notFound: BOX_ITEM_VERIFICATION_ERRORS.ITEM_NOT_IN_ORDER,
      maxReached: BOX_ITEM_VERIFICATION_ERRORS.MAX_VERIFIED_REACHED,
    });

  const onScanShelfHandler = (barcode: string) => scanLocationAndRestock(barcode);

  const onOperatorConfirmFinishHandler = () => {
    dispatch(resetRestockState());
    dispatch(triggerOrderOverviewStage());
  };

  const isAllRestocked = useMemo(() => items.every((i) => i.isRestocked), [items]);
  const sidebarHeaderText = isAllRestocked ? "Lagerinläsning klar!" : "Lagerinläsning";
  const sidebarContentText = isAllRestocked
    ? "Tryck OK för att bekräfta inläsningen"
    : "Läs in följande artiklar på lagerplats";
  return (
    <>
      <main className="control-app__content">
        {items.map((item) => (
          <RestockItem item={item} isSelected={item.id === activeItemId} />
        ))}
      </main>
      <RestockSidebar
        isAllRestocked={isAllRestocked}
        header={sidebarHeaderText}
        content={sidebarContentText}
        isItemScanned={!!activeItemId && activeItemId > -1}
      >
        <RestockActions
          isAllRestocked={isAllRestocked}
          scannedItemId={activeItemId}
          onScanItem={onScanItemHandler}
          onScanShelf={onScanShelfHandler}
          onOperatorConfirmFinish={onOperatorConfirmFinishHandler}
        />
      </RestockSidebar>
    </>
  );
};

export default RestockOverview;
